import { useContext, useState } from 'react';
import { Formik } from 'formik';
import { Stack, Switch } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { api } from '../../services/api';
import { Button } from '../Button';
import { TextComponent } from '../TextComponent';
import { InputComponent } from '../InputComponent';
import { UserDataContext } from '../../providers/userData';
import { LoadingContext } from '../../providers/loading';
import { Contract } from '../../types/ContractType';
import { Project } from '../../types/ProjectType';
import { useTheme } from '../../providers/theme';
import { UpdateProjectModalSchema, UpdateContractModalSchema } from './UpdateModalSchema';
import { BoxEditModal, ContentModal, Footer, HeaderModal, InputContainer, ModalContainer } from './styles';
import { useTranslation } from 'react-i18next';

interface UpdateDataModalType {
  onEditProject?: boolean;
  onEditContract?: boolean;
  dataProject?: Project;
  dataContract?: Contract;
  isOpen: boolean;
  toggle: () => void;
  getContractsData: () => void;
  getProjectsData: () => void;
}

export function UpdateDataModal({
  onEditProject, onEditContract, dataProject, dataContract, isOpen, toggle, getContractsData, getProjectsData
}: UpdateDataModalType) {
  const { contractId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const { userData } = useContext(UserDataContext);
  const { theme } = useTheme();
  const { t } = useTranslation();

  const [projectTitle] = useState(dataProject?.title);
  const [projectDescription] = useState(dataProject?.description);
  const [isProjectActive, setIsProjectActive] = useState(dataProject?.is_active);
  const [contractTitle] = useState(dataContract?.number);

  async function updateProjectData(values: { projectTitle: string | undefined, projectDescription: string | undefined }) {
    setIsLoading({ value: true });
    toggle();
    await api.put(`/contracts/${contractId}/projects?id=${dataProject?.id}`, {
      title: values.projectTitle,
      description: values.projectDescription,
      is_active: isProjectActive,
      owner_id: userData.userEmail,
    })
      .then(() => {
        getProjectsData();
      })
      .catch(error => console.log(error));
  }

  async function updateContractData(values: { contractTitle: string | undefined }) {
    setIsLoading({ value: true });
    toggle();
    await api.put(`/contracts?id=${dataContract?.id}`, {
      number: values.contractTitle,
      owner_id: userData.userEmail
    })
      .then(() => {
        getContractsData();
        setIsLoading({ value: false });
      })
      .catch(error => console.log(error));
  }

  return (
    <>
      {
        isOpen && onEditProject ? (
          <Formik
            initialValues={{
              projectTitle: projectTitle,
              projectDescription: projectDescription,
              owner_id: userData.userEmail || ''
            }}
            validationSchema={UpdateProjectModalSchema}
            validateOnChange={true}
            validateOnMount={true}
            onSubmit={values => updateProjectData(values)}
          >
            {({ errors, touched, handleSubmit, handleChange, values }) => (
              <ModalContainer>
                <BoxEditModal>
                  <HeaderModal>
                    <span style={{ flexGrow: 1, textAlign: 'center' }}>{t('projects-page-edit')}</span>
                    <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
                  </HeaderModal>

                  <form onSubmit={handleSubmit}>
                    <ContentModal>
                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('projects-edit-title')}
                        </TextComponent>
                        <InputComponent
                          id='projectTitle'
                          type='text'
                          value={values.projectTitle}
                          onChange={handleChange}
                        />
                        {errors.projectTitle && touched.projectTitle ? (
                          <div style={{ marginBottom: '10px' }}>
                            <span style={{ fontSize: '12px' }}>{errors.projectTitle}</span>
                          </div>
                        ) : null}
                      </InputContainer>

                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('projects-edit-description')}
                        </TextComponent>
                        <InputComponent
                          id='projectDescription'
                          type='text'
                          value={values.projectDescription}
                          onChange={handleChange}
                        />
                        {errors.projectDescription && touched.projectDescription ? (
                          <div style={{ marginBottom: '10px' }}>
                            <span style={{ fontSize: '12px' }}>{errors.projectDescription}</span>
                          </div>
                        ) : null}
                      </InputContainer>

                      <TextComponent
                        children='Status'
                        fontWeight='bold'
                        fontSize='14px'
                      />
                      <Stack direction="row" spacing={1} alignItems="center" marginBottom={2}>
                        <Switch
                          id='projectIsActive'
                          name='projectIsActive'
                          value={isProjectActive}
                          onChange={(e) => setIsProjectActive(e.target.checked)}
                          checked={isProjectActive ? true : false}
                          color="warning"
                        />
                        <span>{t('projects-table-active')}</span>
                      </Stack>

                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('projects-edit-manager')}
                      </TextComponent>
                      <InputComponent
                        id='projectOwnerId'
                        type='text'
                        value={userData.userEmail}
                        readOnly
                      />

                      <Footer>
                        <Button
                          id='save'
                          type='submit'
                          color={theme.colors.main}
                          sxhover={theme.colors.hoverMain}
                          sxmargintop='20px'
                        >
                          {isLoading.value ? (
                            <Oval color='#fff' secondaryColor='#fff' height='20px' />
                          ) : t('group-page-button')}
                        </Button>
                      </Footer>
                    </ContentModal>
                  </form>
                </BoxEditModal>
              </ModalContainer>
            )}
          </Formik>
        ) : onEditContract && (
          <Formik
            initialValues={{
              contractTitle: contractTitle,
              owner_id: userData.userEmail || ''
            }}
            validationSchema={UpdateContractModalSchema}
            validateOnChange={true}
            validateOnMount={true}
            onSubmit={values => updateContractData(values)}
          >
            {({ errors, touched, handleSubmit, handleChange, values }) => (
              <ModalContainer>
                <BoxEditModal>
                  <HeaderModal>
                    <span style={{ flexGrow: 1, textAlign: 'center' }}>{t('group-page-edit')}</span>
                    <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
                  </HeaderModal>

                  <form onSubmit={handleSubmit}>
                    <ContentModal>
                      <InputContainer>
                        <TextComponent
                          fontWeight='bold'
                          fontSize='14px'
                        >
                          {t('group-page-edit-title')}
                        </TextComponent>
                        <InputComponent
                          id='contractTitle'
                          type='text'
                          value={values.contractTitle}
                          onChange={handleChange}
                        />
                        {errors.contractTitle && touched.contractTitle ? (
                          <div style={{ marginBottom: '10px' }}>
                            <span style={{ fontSize: '12px' }}>{errors.contractTitle}</span>
                          </div>
                        ) : null}
                      </InputContainer>

                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('group-page-edit-manager')}
                      </TextComponent>
                      <InputComponent
                        id='projectOwnerId'
                        type='text'
                        value={userData.userEmail}
                        readOnly
                      />

                      <Footer>
                        <Button
                          id='save'
                          type='submit'
                          color={theme.colors.main}
                          sxhover={theme.colors.hoverMain}
                          sxmargintop='20px'
                        >
                          {isLoading.value ? (
                            <Oval color='#fff' secondaryColor='#fff' height='20px' />
                          ) : t('group-page-button')}
                        </Button>
                      </Footer>
                    </ContentModal>
                  </form>
                </BoxEditModal>
              </ModalContainer>
            )}
          </Formik>
        )
      }
    </>
  );
}
